/**
 * NA-382: the question-text override wiring, in one place for all four
 * bank-backed block editors.
 *
 * Every bank-backed type names the field `description` and treats it
 * identically, so the alternative to this hook is the same four handlers copied
 * into four editors — which is how a field ends up unlocked on three types and
 * locked on the fourth. (NA-313 needed per-type field names for the explanation;
 * question text does not.)
 *
 * Three modes, because ownership means something different in each:
 *
 * - **modal** — the editor is authoring the BANK value, so edits write straight
 *   through with no ownership marker and no revert-on-empty.
 * - **block** (normal English editing) — the full override model: edits claim
 *   ownership, and the Edited badge and revert are offered. Clearing the field is
 *   a legitimate override to blank, exactly as for the correct-answer
 *   explanation; the learner is then shown no question text.
 * - **translation** — the field is a plain translatable block field, and only for
 *   a block that already owns it (`question-field-block-override`: translation is
 *   not offered for a field the block has not overridden). Ownership lives in the
 *   default-locale scope, so a localized edit must neither claim nor release it.
 */

import { useCallback } from 'react';
import type { JSONContent } from '@tiptap/core';
import {
  useFieldOverrideState,
  type FieldOverrideNames,
  type FieldOverrideState,
} from './useFieldOverrideState';

/**
 * The shape the hook needs, rather than a union of the four block types: all four
 * carry these three keys identically, and naming them here keeps
 * `useFieldOverrideState`'s `keyof TBlock` check meaningful.
 */
export interface QuestionTextBearingBlock {
  description?: JSONContent;
  descriptionOverridden?: boolean;
  descriptionBankValue?: JSONContent;
}

export const QUESTION_TEXT_OVERRIDE_NAMES = {
  value: 'description',
  marker: 'descriptionOverridden',
  bankValue: 'descriptionBankValue',
} as const satisfies FieldOverrideNames<QuestionTextBearingBlock>;

export interface QuestionTextOverride {
  override: FieldOverrideState<JSONContent>;
  /**
   * Whether the field renders as an editable rich-text field at all. False only
   * in translation mode for a block that does not own the question text, where
   * the bank's localized value is shown as read-only context instead.
   */
  isEditable: boolean;
  /** Value to render in the field. */
  content: JSONContent | undefined;
  /** Per-keystroke handler. Claims ownership in block mode; writes through in modal mode. */
  onContentChange: (content: JSONContent) => void;
  onRevert: () => void;
  /**
   * Whether to show the Edited badge and its revert action at all.
   *
   * False in the modal (nothing to diverge from) AND in translation mode:
   * reverting RELEASES ownership, which only the default-locale scope may do.
   * Offering it in a localized view produced a blank field and a failed save —
   * the revert patch reduces to `{description: undefined}`, which the block
   * translation endpoint rejects as an empty patch.
   */
  showStatus: boolean;
}

export function useQuestionTextOverride(
  block: QuestionTextBearingBlock,
  onChange: (updates: Record<string, unknown>) => void,
  isModal: boolean,
  translationMode = false,
): QuestionTextOverride {
  const override = useFieldOverrideState<QuestionTextBearingBlock>(
    block,
    QUESTION_TEXT_OVERRIDE_NAMES,
  );

  // Ownership is written only while editing the default locale on a block.
  const writesOwnership = !isModal && !translationMode;

  const onContentChange = useCallback(
    (content: JSONContent) => onChange(
      writesOwnership ? override.buildEdit(content) : { description: content },
    ),
    [onChange, override, writesOwnership],
  );

  const onRevert = useCallback(
    () => onChange(override.buildRevert()),
    [onChange, override],
  );

  return {
    override,
    // Translation mode only offers the field for a block that owns it; everywhere
    // else the field is editable (the modal authors the bank value, and normal
    // block editing is the override itself).
    isEditable: isModal || !translationMode || override.isOverridden,
    // In modal mode the block's own value is the only value: there is no bank
    // value to fall back to, since this IS the bank value being authored.
    content: isModal ? block.description : override.displayValue,
    onContentChange,
    onRevert,
    showStatus: writesOwnership,
  };
}
